import type { GameStore } from '../../store/GameStore';
import { getAllAchievements } from '../../core/systems/AchievementSystem';

/**
 * Achievements panel — shows all achievements with locked/unlocked state.
 */
export class AchievementsPanel {
  private element: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private store: GameStore;
  private visible: boolean = false;

  constructor(parent: HTMLElement, store: GameStore) {
    this.store = store;

    this.element = document.createElement('div');
    this.element.className = 'panel';
    this.element.style.cssText = 'position:absolute;top:40px;right:0;bottom:0;width:300px;transform:translateX(100%);transition:transform var(--transition-normal);pointer-events:auto;z-index:10;';

    const header = document.createElement('div');
    header.className = 'panel__header';
    const title = document.createElement('span');
    title.textContent = 'Achievements';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--icon';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.element.appendChild(header);

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'panel__body';
    this.bodyEl.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
    this.element.appendChild(this.bodyEl);

    parent.appendChild(this.element);
  }

  show(): void {
    this.visible = true;
    this.element.style.transform = 'translateX(0)';
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.element.style.transform = 'translateX(100%)';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  get isVisible(): boolean { return this.visible; }

  render(): void {
    if (!this.visible) return;
    this.bodyEl.innerHTML = '';

    const state = this.store.getState();
    const unlocked = state.achievements ?? [];
    const all = getAllAchievements();

    // Count
    const countEl = document.createElement('div');
    countEl.style.cssText = 'font-size:11px;color:var(--color-text-muted);text-align:center;padding:4px 0;';
    countEl.textContent = `${unlocked.length} / ${all.length} unlocked`;
    this.bodyEl.appendChild(countEl);

    // Show unlocked first, then locked
    const sorted = [...all].sort((a, b) => {
      const aUnlocked = unlocked.includes(a.id) ? 0 : 1;
      const bUnlocked = unlocked.includes(b.id) ? 0 : 1;
      return aUnlocked - bUnlocked;
    });

    for (const ach of sorted) {
      const isUnlocked = unlocked.includes(ach.id);
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = `padding:8px;display:flex;gap:8px;align-items:center;${isUnlocked ? '' : 'opacity:0.4;'}`;

      const icon = document.createElement('span');
      icon.textContent = isUnlocked ? ach.icon : '🔒';
      icon.style.cssText = 'font-size:20px;flex-shrink:0;width:28px;text-align:center;';
      card.appendChild(icon);

      const info = document.createElement('div');
      info.style.cssText = 'flex:1;min-width:0;';

      const name = document.createElement('div');
      name.textContent = ach.name;
      name.style.cssText = `font-size:12px;font-weight:600;${isUnlocked ? 'color:var(--color-text-primary);' : 'color:var(--color-text-muted);'}`;
      info.appendChild(name);

      const desc = document.createElement('div');
      desc.textContent = ach.description;
      desc.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
      info.appendChild(desc);

      card.appendChild(info);

      if (isUnlocked) {
        const check = document.createElement('span');
        check.textContent = '✓';
        check.style.cssText = 'color:var(--color-success);font-weight:700;font-size:14px;';
        card.appendChild(check);
      }

      this.bodyEl.appendChild(card);
    }
  }

  destroy(): void {
    this.element.remove();
  }
}
